import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Megaphone, Send, Users, Mail, Loader2, Clock, Trash2, X } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const MAX = 160;

/**
 * Baker Broadcasts composer.
 * Sellers write a ≤160-char update; the backend fans it out to:
 *  - registered users who favorited this baker (in-app notification)
 *  - guest email subscribers (Resend email with unsubscribe link)
 * Rate-limited to 1 broadcast per 24 hours per baker.
 */
export default function BroadcastComposer() {
  const [audience, setAudience] = useState(null);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [followers, setFollowers] = useState(null);
  const [clearing, setClearing] = useState(false);

  const reload = async () => {
    try {
      const [a, h, f] = await Promise.all([
        api.get("/broadcasts/preview-audience"),
        api.get("/broadcasts/mine"),
        api.get("/broadcasts/followers"),
      ]);
      setAudience(a.data);
      setHistory(h.data || []);
      setFollowers(f.data);
    } catch { /* leave as null */ }
  };
  useEffect(() => { reload(); }, []);

  const send = async () => {
    const clean = msg.trim();
    if (clean.length < 2) { toast.error("Write at least 2 characters"); return; }
    if (!window.confirm(`Send this update to ${audience?.total ?? "everyone who favorited you"}?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/broadcasts", { message: clean });
      toast.success(`Broadcast sent — ${data.notified} in-app · ${data.emailed} email${data.emailed === 1 ? "" : "s"}`);
      setMsg("");
      await reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't send — you may have already sent one in the last 24 hours.");
    }
    setSending(false);
  };

  const remaining = MAX - msg.length;

  const removeGuest = async (email) => {
    if (!window.confirm(`Remove ${email} from your subscribers?`)) return;
    try {
      await api.delete(`/broadcasts/followers/guest?email=${encodeURIComponent(email)}`);
      toast.success("Removed");
      await reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const clearGuests = async () => {
    if (!followers || !followers.guests || !followers.guests.length) return;
    if (!window.confirm(`Clear ALL ${followers.guests.length} guest email subscriber${followers.guests.length === 1 ? "" : "s"}? This can't be undone. (Registered members who favorited you will stay on the list — they can unfavorite themselves.)`)) return;
    setClearing(true);
    try {
      const { data } = await api.post("/broadcasts/followers/clear-guests");
      toast.success(`Cleared ${data.cleared} subscriber${data.cleared === 1 ? "" : "s"}`);
      await reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setClearing(false);
  };

  return (
    <section className="cp-card p-5 sm:p-6" data-testid="broadcast-composer">
      <div className="flex items-center gap-2 mb-1">
        <Megaphone className="w-5 h-5 text-[#FF7E67]" />
        <h2 className="font-heading text-xl text-[#3B2C24]">Broadcast to your followers</h2>
      </div>
      <p className="text-xs text-[#857163] mb-4">
        Send a short update (up to {MAX} chars) to everyone who favorited you. Limit: one per 24 hours.
      </p>

      <div className="grid sm:grid-cols-[1fr_180px] gap-3 mb-2">
        <div>
          <textarea
            value={msg}
            onChange={(e) => setMsg(e.target.value.slice(0, MAX))}
            rows={3}
            placeholder="e.g. Fresh strawberry cake pops drop tomorrow at 10 AM — first come first served!"
            className="cp-input"
            data-testid="broadcast-input"
          />
          <div className={`text-right text-xs mt-1 ${remaining < 20 ? "text-[#C2185B]" : "text-[#857163]"}`}>{remaining} left</div>
        </div>
        <div className="rounded-2xl bg-[#F9EFE6]/60 border border-[#EBDCD1] p-3 text-xs">
          <div className="font-semibold text-[#3B2C24] mb-1.5">This reaches</div>
          {audience ? (
            <>
              <div className="flex items-center gap-1.5 text-[#3B2C24]"><Users className="w-3.5 h-3.5 text-[#8EAC50]" /> {audience.registered} member{audience.registered === 1 ? "" : "s"}</div>
              <div className="flex items-center gap-1.5 text-[#3B2C24] mt-0.5"><Mail className="w-3.5 h-3.5 text-[#2F80ED]" /> {audience.guests} guest{audience.guests === 1 ? "" : "s"} via email</div>
              <div className="mt-1.5 font-semibold text-[#FF7E67]">Total: {audience.total}</div>
            </>
          ) : (
            <div className="text-[#857163]">Loading…</div>
          )}
        </div>
      </div>

      <button
        onClick={send}
        disabled={sending || msg.trim().length < 2}
        className="cp-btn-primary inline-flex items-center gap-1.5 disabled:opacity-60"
        data-testid="broadcast-send"
      >
        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        Send broadcast
      </button>

      {history.length > 0 && (
        <div className="mt-6">
          <div className="text-xs uppercase tracking-widest text-[#857163] mb-2">Recent broadcasts</div>
          <ul className="space-y-2">
            {history.map((b) => (
              <li key={b.id} className="text-sm p-3 rounded-2xl bg-[#F9EFE6]/60 border border-[#EBDCD1]" data-testid={`broadcast-history-${b.id}`}>
                <div className="text-[#3B2C24]">{b.message}</div>
                <div className="text-[10px] text-[#857163] mt-1 flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-0.5"><Clock className="w-3 h-3" /> {(() => { try { return formatDistanceToNow(new Date(b.created_at), { addSuffix: true }); } catch { return ""; } })()}</span>
                  <span>· {b.notified} in-app · {b.emailed} email{b.emailed === 1 ? "" : "s"}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Subscribers management */}
      <div className="mt-6 pt-6 border-t border-[#EBDCD1]" data-testid="subscribers-panel">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-[#8EAC50]" />
            <h3 className="font-semibold text-[#3B2C24] text-sm">Your subscribers</h3>
          </div>
          {followers && followers.guests.length > 0 && (
            <button
              onClick={clearGuests}
              disabled={clearing}
              className="inline-flex items-center gap-1 text-xs text-[#C2185B] hover:opacity-80 disabled:opacity-50"
              data-testid="clear-guest-subs"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Clear guest list
            </button>
          )}
        </div>

        {!followers ? (
          <div className="text-xs text-[#857163]">Loading…</div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
              <div className="rounded-2xl bg-[#F1F7E5] border border-[#8EAC50]/20 p-3">
                <div className="text-xs text-[#5E7A2E] uppercase tracking-widest font-semibold">Members</div>
                <div className="text-2xl font-bold text-[#3B2C24] mt-0.5">{followers.registered_count}</div>
                <div className="text-[10px] text-[#857163]">favorited you on the map</div>
              </div>
              <div className="rounded-2xl bg-[#EAF3FE] border border-[#2F80ED]/20 p-3">
                <div className="text-xs text-[#1D5FBF] uppercase tracking-widest font-semibold">Guest emails</div>
                <div className="text-2xl font-bold text-[#3B2C24] mt-0.5">{followers.guests.length}</div>
                <div className="text-[10px] text-[#857163]">signed up via email</div>
              </div>
            </div>

            {followers.guests.length > 0 ? (
              <ul className="rounded-2xl border border-[#EBDCD1] overflow-hidden bg-white">
                {followers.guests.slice(0, 25).map((g) => (
                  <li key={g.email} className="flex items-center justify-between px-3 py-2 border-b last:border-b-0 border-[#EBDCD1]/50 text-sm" data-testid={`guest-${g.email}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <Mail className="w-3.5 h-3.5 text-[#2F80ED] shrink-0" />
                      <span className="truncate text-[#3B2C24]">{g.email}</span>
                    </div>
                    <button
                      onClick={() => removeGuest(g.email)}
                      title="Remove"
                      className="text-[#857163] hover:text-[#C2185B] p-1"
                      data-testid={`remove-guest-${g.email}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </li>
                ))}
                {followers.guests.length > 25 && (
                  <li className="px-3 py-2 text-xs text-[#857163] text-center bg-[#F9EFE6]/40">
                    …and {followers.guests.length - 25} more
                  </li>
                )}
              </ul>
            ) : (
              <div className="text-xs text-[#857163]">
                No guest email subscribers yet. When visitors favorite you on the map without an account, they'll be asked for their email.
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
